import axios from "axios";

/* ---------------- API base URL (tenant aware) ----------------
   The backend works out which institute you belong to from the request's Host header
   alone — it ignores Origin and Referer. So a page opened at

     https://srm.talentiq.g-axis.in/

   has to send its API calls to

     https://srm.talentiq.g-axis.in/api/Login

   A hardcoded base URL pointed every tenant at the bare domain, where the backend
   resolves no institute and answers 403 "Please use your institute's Talent IQ domain."
   Reusing window.location.hostname keeps each tenant on its own subdomain.

   The base URL is decided once, at startup, in this order:

     1. REACT_APP_API_BASE_URL, when set. Put it in .env.local to point your machine at
        an ngrok tunnel, a LAN IP, or any other backend without editing this file.
     2. A known Talent IQ domain (see TALENTIQ_DOMAINS): reuse the page's protocol +
        hostname and add API_BASE_PATH. No port — every host is proxied on the standard port.
     3. Anything else — localhost, 127.0.0.1, a LAN IP, an ngrok preview — falls back to
        DEV_FALLBACK_BASE_URL, because the dev server and the API are on different hosts.

   Every request goes through this one axios instance, so the interceptors below —
   including the /RefreshToken call — automatically use the tenant hostname too. */

// The Talent IQ domains. The bare domain and every institute subdomain serve the API
// under API_BASE_PATH on the standard port.
const TALENTIQ_DOMAINS = [
  "talentiq.g-axis.in", // dev / staging
  "talentiq.3edge.in", // production
];
const API_BASE_PATH = "/api/";

// Used only when the page is NOT served from a Talent IQ domain (local dev, ngrok).
// Set REACT_APP_API_BASE_URL in .env.local to point at your own tunnel or LAN IP rather
// than editing this line — .env.local is gitignored, so it keeps one developer's backend
// out of everyone else's build.

// baseURL: 'https://pseudodiastolic-overcivil-brinda.ngrok-free.dev/',

// const DEV_FALLBACK_BASE_URL = "https://pseudodiastolic-overcivil-brinda.ngrok-free.dev";
const DEV_FALLBACK_BASE_URL = "https://talentiq.g-axis.in/api/";
// const DEV_FALLBACK_BASE_URL = "https://srm.talentiq.g-axis.in/api/";


const resolveBaseURL = () => {
  if (process.env.REACT_APP_API_BASE_URL) return process.env.REACT_APP_API_BASE_URL;

  // No window under test runners / SSR — nothing to derive a hostname from.
  if (typeof window === "undefined") return DEV_FALLBACK_BASE_URL;

  const { protocol, hostname } = window.location;
  const host = hostname.toLowerCase();

  // Match the domain itself ("talentiq.g-axis.in") and any institute subdomain
  // ("srm.talentiq.g-axis.in", "vit.talentiq.g-axis.in", …). The leading dot stops a
  // lookalike host such as "nottalentiq.g-axis.in" from matching.
  const isTalentIqHost = TALENTIQ_DOMAINS.some(
    (domain) => host === domain || host.endsWith(`.${domain}`)
  );
  if (!isTalentIqHost) return DEV_FALLBACK_BASE_URL;

  //   talentiq.g-axis.in       -> https://talentiq.g-axis.in/api/
  //   srm.talentiq.g-axis.in   -> https://srm.talentiq.g-axis.in/api/
  //   vit.talentiq.g-axis.in   -> https://vit.talentiq.g-axis.in/api/
  //   talentiq.3edge.in        -> https://talentiq.3edge.in/api/
  //   srm.talentiq.3edge.in    -> https://srm.talentiq.3edge.in/api/
  // The subdomain is never parsed — it is carried through from window.location, so a new
  // institute needs no code change. Either way the Host header stays the hostname the page
  // was opened at, which is the only thing the backend uses to resolve the institute.
  return `${protocol}//${host}${API_BASE_PATH}`;
};

const api = axios.create({
  baseURL: resolveBaseURL(),
  timeout: 300000,
  headers: {
    "ngrok-skip-browser-warning": "true",
  },
});

/* ---------------- Token refresh ----------------
   Backend contract: POST /RefreshToken with the current Bearer token returns
   { status: "success", access_token, token_type: "bearer" }; the new token is
   valid 24h from refresh. We refresh proactively when the current token has
   < 30 min remaining (checked on each outgoing request) plus a periodic safety
   net while the user is active. On 401 the response interceptor clears + redirects
   to login. Start/stop are driven by App.js (token present) and logout. */

const REFRESH_THRESHOLD_MS = 30 * 60 * 1000; // refresh when < 30 min remaining
const REFRESH_INTERVAL_MS = 75 * 60 * 1000; // periodic refresh (within the 60–90 min window)

let refreshTimer = null;
let refreshInFlight = null; // dedupes concurrent refreshes into one request

// Decode a JWT's exp claim → epoch ms. Returns null for non-JWT / missing exp.
const getTokenExpMs = (token) => {
  try {
    const payload = token.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const { exp } = JSON.parse(json);
    return exp ? exp * 1000 : null;
  } catch (e) {
    return null;
  }
};

const msUntilExpiry = (token) => {
  const exp = getTokenExpMs(token);
  return exp == null ? null : exp - Date.now();
};

// POST /RefreshToken once; concurrent callers share the in-flight promise.
export const refreshAccessToken = () => {
  if (refreshInFlight) return refreshInFlight;
  if (!localStorage.getItem("token")) return Promise.resolve(null);

  refreshInFlight = api
    .post("/RefreshToken", {}, { _skipProactiveRefresh: true })
    .then((res) => {
      const newToken = res?.data?.access_token;
      if (res?.data?.status === "success" && newToken) {
        localStorage.setItem("token", newToken);
        return newToken;
      }
      return null;
    })
    .catch(() => null) // 401 is handled by the response interceptor (clears + redirects)
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
};

export const startTokenRefresh = () => {
  if (refreshTimer) return; // already running
  refreshTimer = setInterval(() => {
    if (localStorage.getItem("token")) refreshAccessToken();
    else stopTokenRefresh();
  }, REFRESH_INTERVAL_MS);
};

export const stopTokenRefresh = () => {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
};

api.interceptors.request.use(
  async (config) => {
    // Proactively refresh a near-expiry token before sending the request.
    // Skip for the /RefreshToken call itself to avoid a loop.
    if (!config._skipProactiveRefresh) {
      const current = localStorage.getItem("token");
      const remaining = current ? msUntilExpiry(current) : null;
      if (current && remaining != null && remaining < REFRESH_THRESHOLD_MS) {
        await refreshAccessToken();
      }
    }
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Covers a 401 from /RefreshToken (token already expired) and any other
      // expired-session response: stop refreshing, clear, and bounce to login.
      stopTokenRefresh();
      localStorage.clear();
      // Hand the reason to the login screen instead of a blocking alert().
      sessionStorage.setItem("authMessage", "Session expired. Please log in again.");
      window.location.href = "/";
      return Promise.reject(error);
    }
    // Network / timeout / unreachable failures bubble up to the calling catch
    // block. The caller surfaces them via showSnackbar (which rewrites raw
    // axios strings into a proper sentence). We deliberately do NOT trigger
    // the global NetworkStatusProvider overlay for these — only a genuine
    // browser offline event should bring up that full-screen card.
    return Promise.reject(error);
  },
);

export default api;
